import type { ComponentType } from 'react'
import type { PageMeta } from './meta'

/** Public marketing pages. Each one is rendered to its own HTML file at build
 * time (scripts/prerender.mjs), so crawlers and link previews get the text
 * without running JavaScript; in the browser React hydrates that markup.
 * Every other URL is the cabinet, a client-rendered SPA. */

export interface PageModule {
  default: ComponentType
  meta: PageMeta
}

export interface SitePage {
  path: string
  /** Module behind `load`, as Vite's manifest keys it: the prerender step
   * links the page's CSS and preloads its chunk from there. */
  src: string
  load: () => Promise<PageModule>
}

export const NOT_FOUND_PATH = '/404'

/** Content pages are files: src/site/pages/dlya/avtoservisu.tsx is
 * /dlya/avtoservisu, pages/blog/index.tsx is /blog. Each exports a default
 * component and `meta`. */
const CONTENT = import.meta.glob<PageModule>('./pages/**/*.tsx')

const contentPages: SitePage[] = Object.entries(CONTENT).map(([file, load]) => ({
  path: file.replace(/^\.\/pages/, '').replace(/\.tsx$/, '').replace(/\/index$/, ''),
  src: `src/site/${file.slice(2)}`,
  load,
}))

export const SITE_PAGES: SitePage[] = [
  ...contentPages,
  {
    path: '/',
    src: 'src/landing/LandingPage.tsx',
    load: () => import('@/landing/LandingPage'),
  },
  {
    path: '/privacy',
    src: 'src/landing/LegalPage.tsx',
    load: () => import('@/landing/LegalPage').then((m) => ({ default: m.PrivacyPage, meta: m.privacyMeta })),
  },
  {
    path: '/terms',
    src: 'src/landing/LegalPage.tsx',
    load: () => import('@/landing/LegalPage').then((m) => ({ default: m.TermsPage, meta: m.termsMeta })),
  },
  {
    // Written to dist/404.html; nginx serves it with status 404 for any URL
    // that is neither a public page nor a cabinet route.
    path: NOT_FOUND_PATH,
    src: 'src/site/NotFoundPage.tsx',
    load: () => import('@/site/NotFoundPage'),
  },
]

/** `/faq/` and `/faq` are the same page. */
export function normalizePath(pathname: string): string {
  return pathname.replace(/\/+$/, '') || '/'
}

export function findSitePage(pathname: string): SitePage | undefined {
  const path = normalizePath(pathname)
  return SITE_PAGES.find((p) => p.path === path)
}

/** Same key as lib/api.ts. Read directly so the public pages don't pull in
 * the cabinet's API client just to answer "is someone signed in". */
const ACCESS_KEY = 'vp.access_token'

export function hasSession(): boolean {
  try {
    return Boolean(localStorage.getItem(ACCESS_KEY))
  } catch {
    return false
  }
}

/** A signed-in user on `/` gets the cabinet, as before the site was
 * prerendered; `/welcome` still shows them the landing. */
export function opensCabinet(page: SitePage | undefined): boolean {
  return !page || (page.path === '/' && hasSession())
}
